import { useInputBarQuestionStore } from "./input-bar-question-store";
/**
 * Zustand store for canvas-only state: React Flow nodes (geometry,
 * selection, z-order), edges, viewport and cursors. The focus presentation
 * lives in `stores/presentation-store.ts`.
 *
 * Frame data lives in the frames store (`stores/frames-store.ts`); `nodes`
 * follow it through `stores/canvas-frame-nodes.ts`, which is the only writer
 * of `node.data`. Server frames enter through `lib/frame-ingest.ts`.
 */

import { create } from "zustand";
import { retainDesktopInteractionOwnership } from "@/lib/desktop-interaction-cursor";
import { isActiveGuideCursor } from "@/lib/presentation-guide-cursor-lifetime";
import { mirroredStore } from "@/lib/mirrored-store";
import { usePresentationStore } from "@/stores/presentation-store";
import { useSelectionStore } from "@/stores/selection-store";
import type { Node, Edge, Viewport } from "@xyflow/react";
import type { Connection } from "@/api/spaces";
import type { FrameData } from "@/lib/frame-data";
import type { PresentationRestore } from "@/lib/focus/focus-types";
import { setFrameHidden } from "@/lib/frame-visibility";
import { useProjectStore } from "@/stores/project-store";
import { getWindowDisplayId } from "@/desktop/use-window-role";
import type { CanvasKeptOnSwitch } from "@/desktop/owner-extra-projects";
import {
  SELECTED_FRAME_Z_BASE,
  demotedZIndex,
} from "@/stores/canvas-selection-z";
import {
  followFramesStore,
  linkedFrameNodes,
} from "@/stores/canvas-frame-nodes";
import { useFramesStore } from "@/stores/frames-store";
import { isFrameHiddenByMetadata } from "@/stores/frames-selectors";
import {
  keepAskedPoint,
  type DesktopApprovalFields,
} from "@/lib/desktop-approval-question";
import {
  endPresentation,
  finishCursorToolCalls,
  isCursorQuestionTool,
  isFinishedPresentationReplay,
  presentedToolUseId,
  withoutEndedCallLabel,
} from "@/lib/cursor-presented-call";
import {
  agentCursorAnnotationTargets,
  annotationPointerAnchor,
  upsertPresentationAnnotation,
  type AgentAnnotationTarget,
} from "@/lib/agent-annotation";
import type { ActivityLabel } from "@/lib/activity-label";
import { findCanvasTimePageIndex } from "@/components/canvas/canvas-time-pages";
import { useCanvasTimePageStore } from "@/stores/canvas-time-page-store";
import { isWallActive } from "@/stores/workspace-view-store";

/* ---------- Converters ---------- */

function connectionToEdge(conn: Connection): Edge {
  return {
    id: conn.id,
    source: conn.source_frame,
    target: conn.target_frame,
    label: conn.label || undefined,
  };
}

/* ---------- Agent cursor ---------- */

export interface AgentCursorQuestion extends DesktopApprovalFields {
  awaitingInputId: string;
  question: string;
  options: Array<{ id: string; label: string }>;
}

export interface AgentCursor {
  agentId: string;
  /** Project ownership for project-scoped desktop presentations. */
  projectId?: string;
  /** Space this cursor belongs to — used to filter out cross-space cursors. */
  spaceId: string;
  /** Persistent routing metadata retained after live session maps are cleared. */
  executionId?: string;
  sessionId?: string;
  conversationFrameId?: string;
  name: string;
  color: string;
  /** The tool step in English, e.g. "Reading file: a.ts". */
  toolCall?: string;
  /** `toolCall` in the app language, rendered at display time. Set and
   *  cleared together with `toolCall`. */
  toolCallLabel?: ActivityLabel;
  toolName?: string;
  /** The backend's `reads_only`: toolName is a desktop call that only reads
   *  (a look, no action), so it does not take over the cursor. */
  readsOnly?: boolean;
  /** True when the current toolCall reported an error — bubble shows a red dot. */
  toolFailed?: boolean;
  /** Runtime reply chrome yields to desktop interaction for this execution. */
  desktopInteractionExecutionId?: string;
  content?: string;
  /** Full text when the agent's reply exceeds the snippet cap. The
   *  cursor renders `content` by default and surfaces an expand toggle
   *  when this is set, revealing up to ~2000 chars on demand. */
  contentFull?: string;
  targetFrameId: string | null;
  /** Task ID this agent is working on — used for idle cursor anchoring. */
  taskId?: string | null;
  /** ISO timestamp when cursor was last updated */
  lastSeen: string;
  /** Display that owns an unpointed compact cursor once it locks in place. */
  displayId?: number | null;
  /** Monotonically increasing seq from "real" frame actions (create /
   *  update / read). Stays the same on streaming events, so the cursor
   *  animation can re-trigger the working sequence on every fresh action
   *  even when targetFrameId is unchanged. */
  actionSeq?: number;
  /** Set when the execution failed/cancelled — cursor lingers briefly
   *  to show the reason inline before being removed. */
  error?: string;
  /** Cursor-anchored question pinned to this agent's cursor.
   *  Set on `cursor.question` WS events; cleared on resolve/answered. */
  question?: AgentCursorQuestion;
  /** Whether this cursor's execution was started by a user chat or by
   *  background heartbeat. */
  origin?: "chat" | "heartbeat";
  /** Set when the underlying execution has reached a terminal state
   *  (completed / failed / cancelled / paused). The cursor lingers on
   *  screen for the post-completion grace window so the user can read
   *  the final reply (and click the expand arrow on long answers); the
   *  cursor-animation layer uses this flag to STOP following the live
   *  user pointer at that moment, snapping the bubble to the pointer
   *  position from the instant the execution ended. */
  frozen?: boolean;
  /** `lastSeen` value of the terminal result the user has already viewed or
   *  followed up on. A later cursor update changes `lastSeen`, re-arming the
   *  result-attention ring without a separate reset path. */
  resultAcknowledgedAt?: string;
  /** Resolved global desktop point from an assistant [POINT:...] reply.
   *  Stored in desktop points; renderers subtract their own window origin. */
  pointTarget?: {
    displayId: number;
    globalX: number;
    globalY: number;
    seq: number;
    travelDurationMs?: number;
    completionToken?: string;
  };
  /** Last action target, retained as a fallback for desktop approval guidance.
   *  Exists only while the run is live and not frozen, errored or asking a
   *  question. Ordinary streaming follows the user's pointer independently. */
  restPoint?: { displayId: number; globalX: number; globalY: number };
  /** Resolved global desktop annotation rendered in every Tauri mode. */
  annotationTarget?: AgentAnnotationTarget;
  /** Bounded marks retained for the active atomic presentation. */
  annotationTargets?: AgentAnnotationTarget[];
  /** Presented frames the cursor bubble should not cover. */
  exclusionFrameIds?: string[];
  /** Regions owned by the current point/highlight action. */
  annotationExclusionTargets?: Array<{
    displayId: number;
    globalX: number;
    globalY: number;
    width: number;
    height: number;
  }>;
  /** Regions owned by the current frame presentation. */
  frameExclusionTargets?: Array<{
    displayId: number;
    globalX: number;
    globalY: number;
    width: number;
    height: number;
  }>;
  /** Ephemeral pointer created by foreground presentation sequences.
   *  These can be dismissed by the user and are cleared on the next
   *  user-originated request. Runtime cursors are not marked this way. */
  presentation?: "point";
  /** Identifies narration owned by an in-flight atomic presentation.
   *  Runtime progress updates must not replace this copy before delivery is
   *  acknowledged, because the verifier checks the requested text exactly. */
  presentationDeliveryId?: string;
  /** Narration rendered over live runtime status for an atomic presentation. */
  presentationNarration?: string;
  /** The tool call (`tool_use_id`) whose label `toolCall` shows. */
  toolUseId?: string;
  /** The tool call the current presentation speaks for; its label ends with it. */
  presentedToolUseId?: string;
  /** The last call whose presentation ended; its label never shows again. */
  endedToolUseId?: string;
  /** Completed calls in this execution, newest last; labels cannot replay. */
  completedToolUseIds?: string[];
  /** Presentations that finished on this cursor, newest last; never replayed. */
  finishedPresentationIds?: string[];
}

/** The place a cursor last acted, carried past the end of its presentation.
 *  Nothing once the run has ended or paused on a question. */
function restPointOf(cursor: AgentCursor): AgentCursor["restPoint"] {
  if (cursor.frozen || cursor.error || cursor.question) return undefined;
  const target = cursor.pointTarget;
  return target
    ? {
        displayId: target.displayId,
        globalX: target.globalX,
        globalY: target.globalY,
      }
    : cursor.restPoint;
}

/* ---------- Pending directory permission ---------- */

export interface PendingPermission {
  executionId: string;
  blockedDirectories: string[];
  allDirectories: string[];
}

/* ---------- Awaiting input (agent asking the user a question) ---------- */

export interface AwaitingInputOption {
  id: string;
  label: string;
}

export interface AwaitingInput {
  id: string;
  frameId: string;
  /** Project that owns the reply endpoint, retained across target switches. */
  projectId?: string;
  executionId: string;
  /** Conversation that asked (a chat or a task worker); null for other runs. */
  conversationId: string | null;
  /** Task the asking run works on; null outside tasks. */
  taskId: string | null;
  question: string;
  context: string;
  options: AwaitingInputOption[];
  createdAt: string;
  timeoutAt: string;
}

/** System-wide text selection captured at summon time (desktop only). */
export interface SummonSelection {
  text: string;
  appName: string;
  /** Set once the quote has been prepended into the input value by any
   *  window. Travels with the summon through the mirrored store, so a
   *  cross-display re-anchor cannot re-prepend the quote in the
   *  destination window. */
  quoteApplied?: boolean;
}

export type CursorInputSubmitMode = "onboarding_demo_suppress";

/* ---------- Store ---------- */

export interface CanvasState {
  nodes: Node<FrameData>[];
  edges: Edge[];
  viewport: Viewport;

  // Frame selection: ids live in the selection store; these paint the nodes.
  selectFrame: (frameId: string | null) => void;

  // Pan-to-frame callback (set by CanvasViewport which has ReactFlow context)
  panToFrame: ((frameId: string) => void) | null;
  setPanToFrame: (fn: ((frameId: string) => void) | null) => void;

  // Programmatic viewport setter (set by CanvasViewport which has ReactFlow
  // context) — moves the camera to an exact viewport (presentation mode).
  setViewportProgrammatic:
    | ((viewport: Viewport, opts?: { duration?: number }) => void)
    | null;
  setSetViewportProgrammatic: (
    fn: ((viewport: Viewport, opts?: { duration?: number }) => void) | null,
  ) => void;

  /** Select and pan to a frame if it exists on the canvas (on the wall: open it in focus). Returns true if navigated. */
  navigateToFrame: (frameId: string) => boolean;

  // Zoom/pan callbacks (set by CanvasViewport which has ReactFlow context)
  zoomIn: (() => void) | null;
  zoomOut: (() => void) | null;
  fitViewAll: (() => void) | null;
  fitViewNodes: ((nodeIds: string[]) => void) | null;
  setZoomControls: (
    controls: {
      zoomIn: () => void;
      zoomOut: () => void;
      fitViewAll: () => void;
      fitViewNodes: (nodeIds: string[]) => void;
    } | null,
  ) => void;

  // Pan mode (shared between toolbar toggle and viewport)
  isPanMode: boolean;
  togglePanMode: () => void;

  // Comment mode
  commentModeActive: boolean;
  toggleCommentMode: () => void;
  exitCommentMode: () => void;

  // Note placement mode
  notePlacementActive: boolean;
  toggleNotePlacement: () => void;
  exitNotePlacement: () => void;

  // Screen-to-flow coordinate converter (set by CanvasViewport)
  screenToFlowPosition:
    | ((pos: { x: number; y: number }) => { x: number; y: number })
    | null;
  setScreenToFlowPosition: (
    fn: ((pos: { x: number; y: number }) => { x: number; y: number }) | null,
  ) => void;

  // Multi-frame selection (Cmd+click)
  toggleMultiSelect: (frameId: string) => void;
  selectFrames: (frameIds: string[]) => void;
  clearMultiSelect: () => void;
  multiSelectContextMenu: { x: number; y: number } | null;
  openMultiSelectContextMenu: (x: number, y: number) => void;
  closeMultiSelectContextMenu: () => void;

  /** Frame ids whose ResizeObserver "dimensions" echoes must NOT be
   *  persisted — presentation mode resizes are ephemeral. Local-only,
   *  never serialized into store snapshots. */
  presentationDimsSuppressedFrameIds: Set<string>;
  /** Canvas geometry a running presentation puts back when it ends. */
  presentationRestore: PresentationRestore | null;
  addPresentationDimsSuppressed: (frameId: string) => void;
  clearPresentationDimsSuppressed: (frameId: string) => void;

  // Comment popover
  commentPopover: {
    frameId: string;
    frameTitle: string;
    projectId?: string;
    spaceId?: string;
    agentId?: string;
    agentName?: string;
    agentColor?: string;
    position: { x: number; y: number };
    /** Desktop display that opened the popover. Null/undefined means web/single-window. */
    displayId?: number | null;
    /** When set, the popover is filtered to this task's comment thread
     * (inline comment-anchor badge flow). */
    scopedTaskId?: string;
    /** Optional local-only agent chip used by scripted onboarding demos. */
    demoAgent?: {
      id: string;
      name: string;
      color: string;
      avatar_seed?: string;
    };
    /** Demo-only mode: close and emit an event without creating/posting a task. */
    submitMode?: "onboarding_demo_suppress";
    submitEventName?: string;
  } | null;
  openCommentPopover: (data: CanvasState["commentPopover"]) => void;
  closeCommentPopover: () => void;

  // Agent cursors (driven by WebSocket events)
  agentCursors: Map<string, AgentCursor>;
  setAgentCursor: (
    cursor: Omit<AgentCursor, "targetFrameId"> & {
      targetFrameId?: string | null;
    },
  ) => void;
  setAgentCursorPoint: (
    agentId: string,
    point: NonNullable<AgentCursor["pointTarget"]>,
  ) => void;
  acknowledgeAgentCursorResult: (agentId: string) => void;
  /** Remove an agent cursor. A cursor holding an unanswered ask_user
   *  question is kept (the card must stay until the user answers) unless
   *  `force` is passed by an explicit answer/dismiss path. */
  removeAgentCursor: (agentId: string, opts?: { force?: boolean }) => void;
  clearAgentCursors: () => void;
  clearAgentCursorsForSpace: (spaceId: string) => void;
  /** `completed` marks the presentation finished: a replay of it is ignored. */
  clearPresentedCursors: (
    spaceId?: string | null,
    presentationDeliveryId?: string,
    executionId?: string,
    completed?: boolean,
  ) => void;
  finishPresentedCursor: (
    spaceId: string | null | undefined,
    presentationDeliveryId: string,
  ) => void;
  clearPresentedAnnotations: (
    spaceId?: string | null,
    presentationDeliveryId?: string,
    executionId?: string,
  ) => void;
  clearPresentedFrameExclusions: (
    spaceId?: string | null,
    presentationDeliveryId?: string,
  ) => void;
  /** Set/replace a cursor-anchored question on a cursor.
   *
   *  If a cursor for `agentId` already exists, the question is merged in.
   *  If no cursor exists yet (e.g. cross-space rehydration before the
   *  agent has emitted any `cursor.move`), an optional `fallback` shell
   *  may be passed to create a minimal idle cursor entry that holds the
   *  question. The cursor renders as idle (`targetFrameId: null`) until a
   *  real `cursor.move` arrives and merges. Without `fallback` this is a
   *  no-op (with warning) — preserves the original strict behavior for
   *  live `cursor.question` events. */
  setAgentCursorQuestion: (
    agentId: string,
    question: AgentCursorQuestion | null,
    fallback?: { spaceId: string; name: string; color: string },
  ) => void;
  /** Clear the cursor-anchored question on an existing cursor. */
  clearAgentCursorQuestion: (agentId: string) => void;

  // Pending directory permissions (driven by WebSocket events)
  pendingPermissions: Map<string, PendingPermission>;
  addPendingPermission: (permission: PendingPermission) => void;
  removePendingPermission: (executionId: string) => void;

  // Awaiting-input questions per frame (driven by WebSocket events)
  awaitingInputs: Map<string, AwaitingInput[]>;
  addAwaitingInput: (awaiting: AwaitingInput) => void;
  removeAwaitingInput: (awaitingInputId: string) => void;

  /** Cursor input bar. Position is stored in flow coords for submit context;
   *  rendering may either follow that cursor anchor or pin to the bottom of
   *  the active viewport. Only one cursor input bar is shown at a time. */
  cursorInput: {
    flowX: number;
    flowY: number;
    placement?: "cursor" | "anchor" | "bottom";
    summonId?: number;
    displayId?: number | null;
    selection?: SummonSelection;
    agentKey?: string;
    submitMode?: CursorInputSubmitMode;
    submitEventName?: string;
  } | null;
  openCursorInput: (pos: {
    flowX: number;
    flowY: number;
    placement?: "cursor" | "anchor" | "bottom";
    summonId?: number;
    displayId?: number | null;
    selection?: SummonSelection;
    agentKey?: string;
    submitMode?: CursorInputSubmitMode;
    submitEventName?: string;
  }) => void;
  moveCursorInput: (pos: {
    flowX: number;
    flowY: number;
    placement?: "cursor" | "anchor" | "bottom";
    summonId?: number;
    displayId?: number | null;
    selection?: SummonSelection;
    agentKey?: string;
    submitMode?: CursorInputSubmitMode;
    submitEventName?: string;
  }) => void;
  closeCursorInput: () => void;

  /** Window-local screen coords (CSS px, top-left origin) where the
   *  Rust input-bar shortcut path wants to open the cursor input bar.
   *  Buffered here until CanvasViewport can convert the screen anchor into
   *  the single cursorInput representation used by compact/workspace/Denk. */
  pendingCursorSummon: {
    x: number;
    y: number;
    displayId?: number | null;
    placement?: "cursor" | "anchor" | "bottom";
    summonId?: number;
    /** Date.now() at summon, for the 300ms selection-attach window. */
    summonAt?: number;
    selection?: SummonSelection;
    agentKey?: string;
    submitMode?: CursorInputSubmitMode;
    submitEventName?: string;
  } | null;
  setPendingCursorSummon: (
    pos: {
      x: number;
      y: number;
      displayId?: number | null;
      placement?: "cursor" | "anchor" | "bottom";
      summonId?: number;
      /** Date.now() at summon, for the 300ms selection-attach window. */
      summonAt?: number;
      selection?: SummonSelection;
      agentKey?: string;
      submitMode?: CursorInputSubmitMode;
      submitEventName?: string;
    } | null,
  ) => void;
  /** Attach a late-arriving selection capture to whichever summon state
   *  (pending or consumed) carries `summonId`. A selection anchors the bar
   *  at the cursor position captured at the summon point ("cursor"); a
   *  summon with no selection at all keeps "bottom" (unchanged). No-op on
   *  mismatch. The 300ms attach window is enforced by the caller
   *  (`handleSelectionReady`) for pending summons only; once the summon has
   *  been consumed into `cursorInput` (bar is already open), a matching
   *  late selection is accepted unconditionally. */
  applySummonSelection: (summonId: number, selection: SummonSelection) => void;
  /** Mark the selection quote as prepended into the input value, on
   *  whichever summon state (pending or consumed) carries `summonId`.
   *  No-op on mismatch or when no selection is attached. */
  markSummonQuoteApplied: (summonId: number) => void;

  // Agent template IDs whose latest delegation hit the project's parallel-
  // agents cap and is waiting for a Session slot. Driven by the
  // ``execution.deferred`` WS event; cleared when the matching execution
  // transitions out of ``deferred`` via ``execution.status``. Drives the
  // amber dot on AgentChip in the input bar's AgentStack.
  deferredAgentTemplateIds: Set<string>;
  /** Map of deferred execution_id → agent_template_id, used so that we
   *  can clear the badge when the execution is later promoted/finished
   *  even though ``execution.status`` doesn't carry the template id. */
  deferredExecutionAgents: Map<string, string>;
  markAgentDeferred: (executionId: string, agentTemplateId: string) => void;
  clearAgentDeferredByExecution: (executionId: string) => void;

  // Bulk load from API
  loadConnections: (connections: Connection[]) => void;

  /**
   * Frames hidden ephemerally by focus mode / agent presentation.
   * Local-only — never persisted. Cleared by fit view (show all),
   * space switches, and store reset.
   */
  focusHiddenFrameIds: Set<string>;
  /**
   * Replace the ephemeral focus-hidden set and recompute effective
   * visibility (`metadata.hidden || focus-hidden`). Pass `[]` to exit
   * focus mode. Never persisted.
   */
  setFocusHiddenFrames: (frameIds: string[]) => void;

  // Single edge operations
  addConnection: (conn: Connection) => void;
  removeConnection: (connId: string) => void;

  // React Flow callbacks
  setNodes: (nodes: Node<FrameData>[]) => void;
  setEdges: (edges: Edge[]) => void;
  setViewport: (viewport: Viewport) => void;

  clear: (kept?: CanvasKeptOnSwitch) => void;
}

const DEFAULT_VIEWPORT: Viewport = { x: 0, y: 0, zoom: 1 };

/** Paint the selection store's multi-selection onto the nodes, last on top. */
function paintMultiSelection(state: CanvasState): Partial<CanvasState> {
  const { multiSelectedFrameIds: ids, selectionSeq } =
    useSelectionStore.getState();
  return {
    multiSelectContextMenu: null,
    nodes: state.nodes.map((n) => {
      const selectedIndex = ids.indexOf(n.id);
      const shouldSelect = selectedIndex >= 0;
      const newZIndex = shouldSelect
        ? SELECTED_FRAME_Z_BASE + selectionSeq + selectedIndex
        : demotedZIndex(n.zIndex);
      if (n.selected === shouldSelect && n.zIndex === newZIndex) return n;
      return { ...n, selected: shouldSelect, zIndex: newZIndex };
    }),
  };
}
export const useCanvasStore = create<CanvasState>()(
  mirroredStore(
    "canvas",
    linkedFrameNodes((set) => ({
      nodes: [],
      edges: [],
      viewport: DEFAULT_VIEWPORT,
      panToFrame: null,
      setViewportProgrammatic: null,
      zoomIn: null,
      zoomOut: null,
      fitViewAll: null,
      fitViewNodes: null,
      isPanMode: false,
      commentModeActive: false,
      notePlacementActive: false,
      commentPopover: null,
      agentCursors: new Map(),
      pendingPermissions: new Map(),
      awaitingInputs: new Map(),
      deferredAgentTemplateIds: new Set<string>(),
      deferredExecutionAgents: new Map<string, string>(),
      multiSelectContextMenu: null,
      cursorInput: null,
      pendingCursorSummon: null,
      focusHiddenFrameIds: new Set<string>(),

      selectFrame: (frameId) => {
        useSelectionStore.getState().select(frameId);
        const { selectionSeq } = useSelectionStore.getState();
        set((state) => ({
          multiSelectContextMenu: null,
          nodes: state.nodes.map((n) => {
            const shouldSelect = n.id === frameId;
            const newZIndex = shouldSelect
              ? SELECTED_FRAME_Z_BASE + selectionSeq
              : demotedZIndex(n.zIndex);
            if (n.selected === shouldSelect && n.zIndex === newZIndex) return n;
            return { ...n, selected: shouldSelect, zIndex: newZIndex };
          }),
        }));
      },

      toggleMultiSelect: (frameId) => {
        useSelectionStore.getState().toggleMulti(frameId);
        set(paintMultiSelection);
      },

      clearMultiSelect: () => {
        if (useSelectionStore.getState().multiSelectedFrameIds.length === 0) {
          return;
        }
        useSelectionStore.getState().clearMulti();
        set((state) => ({
          multiSelectContextMenu: null,
          nodes: state.nodes.map((n) => {
            const newZIndex = demotedZIndex(n.zIndex);
            if (!n.selected && n.zIndex === newZIndex) return n;
            return { ...n, selected: false, zIndex: newZIndex };
          }),
        }));
      },

      selectFrames: (frameIds) => {
        if (frameIds.length === 0) return;
        useSelectionStore.getState().setMulti(frameIds);
        set(paintMultiSelection);
      },

      openMultiSelectContextMenu: (x, y) =>
        set({ multiSelectContextMenu: { x, y } }),

      closeMultiSelectContextMenu: () => set({ multiSelectContextMenu: null }),

      setPanToFrame: (fn) => set({ panToFrame: fn }),

      setSetViewportProgrammatic: (fn) => set({ setViewportProgrammatic: fn }),

      navigateToFrame: (frameId: string) => {
        if (isWallActive()) {
          // `openFramesNow`'s contract; loaded lazily because the focus core
          // it opens through imports this store.
          void import("@/lib/open-frames").then(({ openFramesNow }) =>
            openFramesNow([frameId], "navigate"),
          );
          return true;
        }
        const state = useCanvasStore.getState();
        const node = state.nodes.find((n) => n.id === frameId);
        if (!state.panToFrame || !node) return false;

        const frame = useFramesStore.getState().framesById[frameId];
        const wasHidden = node.hidden === true;
        // Auto-unhide if the frame is hidden (durably and/or by focus mode)
        if (node.hidden) {
          if (isFrameHiddenByMetadata(frame)) {
            setFrameHidden(frameId, false);
          }
          if (state.focusHiddenFrameIds.has(frameId)) {
            state.setFocusHiddenFrames(
              [...state.focusHiddenFrameIds].filter((id) => id !== frameId),
            );
          }
        }

        const visibleState = useCanvasStore.getState();
        const targetPageIndex = findCanvasTimePageIndex(
          visibleState.nodes,
          frameId,
          useProjectStore.getState().currentSpace?.id,
        );
        if (targetPageIndex !== null) {
          useCanvasTimePageStore.getState().setActivePageIndex(targetPageIndex);
        }

        state.selectFrame(frameId);
        state.panToFrame(frameId);
        if (wasHidden && typeof requestAnimationFrame === "function") {
          requestAnimationFrame(() => {
            if (useSelectionStore.getState().selectedFrameId !== frameId)
              return;
            const next = useCanvasStore.getState();
            if (!next.nodes.some((n) => n.id === frameId && !n.hidden)) return;
            next.panToFrame?.(frameId);
          });
        }
        return true;
      },

      setZoomControls: (controls) =>
        set(
          controls
            ? {
                zoomIn: controls.zoomIn,
                zoomOut: controls.zoomOut,
                fitViewAll: controls.fitViewAll,
                fitViewNodes: controls.fitViewNodes,
              }
            : {
                zoomIn: null,
                zoomOut: null,
                fitViewAll: null,
                fitViewNodes: null,
              },
        ),

      togglePanMode: () =>
        set((state) => ({
          isPanMode: !state.isPanMode,
          commentModeActive: false,
          notePlacementActive: false,
        })),

      toggleCommentMode: () =>
        set((state) => ({
          commentModeActive: !state.commentModeActive,
          notePlacementActive: false,
          isPanMode: false,
        })),

      exitCommentMode: () =>
        set({ commentModeActive: false, commentPopover: null }),

      toggleNotePlacement: () =>
        set((state) => ({
          notePlacementActive: !state.notePlacementActive,
          commentModeActive: false,
          isPanMode: false,
        })),

      exitNotePlacement: () => set({ notePlacementActive: false }),

      screenToFlowPosition: null,
      setScreenToFlowPosition: (fn) => set({ screenToFlowPosition: fn }),

      presentationDimsSuppressedFrameIds: new Set<string>(),
      presentationRestore: null,

      addPresentationDimsSuppressed: (frameId) =>
        set((state) => {
          if (state.presentationDimsSuppressedFrameIds.has(frameId)) {
            return state;
          }
          const next = new Set(state.presentationDimsSuppressedFrameIds);
          next.add(frameId);
          return { presentationDimsSuppressedFrameIds: next };
        }),

      clearPresentationDimsSuppressed: (frameId) =>
        set((state) => {
          if (!state.presentationDimsSuppressedFrameIds.has(frameId)) {
            return state;
          }
          const next = new Set(state.presentationDimsSuppressedFrameIds);
          next.delete(frameId);
          return { presentationDimsSuppressedFrameIds: next };
        }),

      openCommentPopover: (data) =>
        set(() => {
          if (!data) return { commentPopover: null };
          const { currentProject, currentSpace } = useProjectStore.getState();
          return {
            commentPopover: {
              ...data,
              projectId: data.projectId ?? currentProject?.id,
              spaceId: data.spaceId ?? currentSpace?.id,
              displayId:
                data.displayId === undefined
                  ? getWindowDisplayId()
                  : data.displayId,
            },
            commentModeActive: false,
            notePlacementActive: false,
            isPanMode: false,
          };
        }),
      closeCommentPopover: () => set({ commentPopover: null }),

      openCursorInput: (pos) =>
        set({
          cursorInput: {
            flowX: pos.flowX,
            flowY: pos.flowY,
            placement: pos.placement,
            summonId: pos.summonId,
            displayId: pos.displayId,
            selection: pos.selection,
            agentKey: pos.agentKey,
            submitMode: pos.submitMode,
            submitEventName: pos.submitEventName,
          },
        }),
      moveCursorInput: (pos) =>
        set({
          cursorInput: {
            flowX: pos.flowX,
            flowY: pos.flowY,
            placement: pos.placement,
            summonId: pos.summonId,
            displayId: pos.displayId,
            selection: pos.selection,
            agentKey: pos.agentKey,
            submitMode: pos.submitMode,
            submitEventName: pos.submitEventName,
          },
        }),
      closeCursorInput: () => set({ cursorInput: null }),

      setPendingCursorSummon: (pos) => set({ pendingCursorSummon: pos }),

      applySummonSelection: (summonId, selection) =>
        set((state) => {
          if (state.pendingCursorSummon?.summonId === summonId) {
            return {
              pendingCursorSummon: {
                ...state.pendingCursorSummon,
                selection,
                placement: "cursor",
              },
            };
          }
          if (state.cursorInput?.summonId === summonId) {
            return {
              cursorInput: {
                ...state.cursorInput,
                selection,
                // Cursor anchor — the workspace path reuses the flowX/flowY
                // captured at summon for the "cursor" placement.
                placement: "cursor",
              },
            };
          }
          return {};
        }),

      markSummonQuoteApplied: (summonId) =>
        set((state) => {
          if (
            state.pendingCursorSummon?.summonId === summonId &&
            state.pendingCursorSummon.selection
          ) {
            return {
              pendingCursorSummon: {
                ...state.pendingCursorSummon,
                selection: {
                  ...state.pendingCursorSummon.selection,
                  quoteApplied: true,
                },
              },
            };
          }
          if (
            state.cursorInput?.summonId === summonId &&
            state.cursorInput.selection
          ) {
            return {
              cursorInput: {
                ...state.cursorInput,
                selection: {
                  ...state.cursorInput.selection,
                  quoteApplied: true,
                },
              },
            };
          }
          return {};
        }),

      setAgentCursor: (update) =>
        set((state) => {
          const next = new Map(state.agentCursors);
          let prev = next.get(update.agentId);
          if (isFinishedPresentationReplay(prev, update)) return state;
          const cursor = withoutEndedCallLabel(prev, update);

          // Drop any stale cursor whose agentId differs but whose name and
          // spaceId both match the incoming one. This kills the first-turn
          // double cursor on OpenCode/Codex: their CLI-native session id is
          // not a UUID, so the very first cursor frame keys on the dispatch
          // hint UUID while subsequent frames key on the eventual Session.pk
          // — two different keys for the same agent. Without this dedup the
          // backend can produce two visible cursors until the 10s safety-net
          // cleanup fires. The spaceId guard ensures agents in different
          // spaces with the same template name don't clobber each other.
          //
          // Hand pending ask_user state to the surviving entry so a cursor-key
          // migration cannot drop a question.
          let inheritedQuestion: AgentCursor["question"] | undefined;
          if (cursor.name) {
            for (const [existingId, existingCursor] of next) {
              if (
                existingId !== cursor.agentId &&
                existingCursor.name === cursor.name &&
                existingCursor.spaceId === cursor.spaceId
              ) {
                if (
                  !prev &&
                  (cursor.presentation === "point" ||
                    existingCursor.presentationDeliveryId)
                ) {
                  prev = existingCursor;
                }
                if (existingCursor.question && !inheritedQuestion) {
                  inheritedQuestion = existingCursor.question;
                }
                next.delete(existingId);
              }
            }
          }

          // Preserve previous toolCall/content only when the incoming event has
          // neither field (e.g. status-only cursor updates). Assistant content is
          // more meaningful than the preceding tool label, so it clears stale
          // generic labels like "Running command" as soon as text arrives.
          // Preserve previous targetFrameId when the new event doesn't include it
          // (stream events omit it so MCP-set frame targets persist).
          // Ordinary execution updates must not retarget the user's active guide.
          if (isActiveGuideCursor(prev) && cursor.presentation !== "point") {
            return state;
          }
          const resolvedTarget: string | null =
            "targetFrameId" in cursor && cursor.targetFrameId !== undefined
              ? cursor.targetFrameId
              : (prev?.targetFrameId ?? null);
          // When `cursor.error` is set, this is a terminal-state cursor
          // update — drop any stale toolCall/content so the cursor doesn't
          // render the old activity label alongside the red error.
          const isErrorUpdate = !!cursor.error;
          const isPresentationUpdate = cursor.presentation === "point";
          const overlaysPresentationNarration =
            isPresentationUpdate && !!cursor.presentationDeliveryId;
          const preservesPointPresentation =
            prev?.presentation === "point" &&
            !isPresentationUpdate &&
            !cursor.pointTarget &&
            !cursor.annotationTarget &&
            !cursor.targetFrameId;
          const preservesPresentationAnnotations = !!(
            isPresentationUpdate &&
            ((cursor.presentationDeliveryId &&
              cursor.presentationDeliveryId === prev?.presentationDeliveryId) ||
              (cursor.executionId && cursor.executionId === prev?.executionId))
          );
          const annotationTargets = cursor.annotationTarget?.annotationId
            ? upsertPresentationAnnotation(
                preservesPresentationAnnotations
                  ? agentCursorAnnotationTargets(prev)
                  : [],
                cursor.annotationTarget,
              )
            : preservesPresentationAnnotations
              ? prev?.annotationTargets
              : undefined;
          const clearsCursorTarget =
            !preservesPointPresentation &&
            !!(
              cursor.toolCall ||
              cursor.content ||
              cursor.targetFrameId ||
              cursor.error
            );
          const clearsQuestion =
            !isPresentationUpdate &&
            !!(cursor.toolCall || cursor.content || cursor.error);
          // The cursor whose tool step shows; its label travels with it.
          const toolCallSource = isErrorUpdate
            ? undefined
            : isPresentationUpdate
              ? overlaysPresentationNarration
                ? prev
                : cursor
              : cursor.content
                ? undefined
                : cursor.toolCall !== undefined
                  ? cursor
                  : prev;
          const merged: AgentCursor = prev
            ? {
                ...prev,
                ...cursor,
                executionId: cursor.executionId ?? prev.executionId,
                sessionId: cursor.sessionId ?? prev.sessionId,
                toolCall: toolCallSource?.toolCall,
                toolCallLabel: toolCallSource?.toolCallLabel,
                content: isErrorUpdate
                  ? undefined
                  : isPresentationUpdate
                    ? overlaysPresentationNarration
                      ? prev.content
                      : cursor.content
                    : (cursor.content ??
                      (cursor.toolCall ? undefined : prev.content)),
                // Mirror content's preservation rules: drop the full text on
                // errors and on tool-call/content-only events that don't
                // carry their own contentFull; keep prior full text when a
                // new event has no fresh assistant text at all.
                contentFull: isErrorUpdate
                  ? undefined
                  : isPresentationUpdate
                    ? overlaysPresentationNarration
                      ? prev.contentFull
                      : cursor.contentFull
                    : (cursor.contentFull ??
                      (cursor.toolCall || cursor.content
                        ? undefined
                        : prev.contentFull)),
                targetFrameId: resolvedTarget,
                // Explicit error set wins. Any new activity (toolCall or
                // content) clears a stale error. Without this the error
                // would survive the agent recovering on a follow-up turn.
                error: isPresentationUpdate
                  ? overlaysPresentationNarration
                    ? prev.error
                    : cursor.error
                  : (cursor.error ??
                    (cursor.toolCall || cursor.content
                      ? undefined
                      : prev.error)),
                // Per-tool failure dot follows the same lifecycle: a fresh
                // toolCall/content clears the previous tool's dot unless the
                // incoming payload flags the new tool as failed; a
                // tool_failed-only payload preserves the label and sets dot.
                toolFailed: isErrorUpdate
                  ? undefined
                  : isPresentationUpdate && overlaysPresentationNarration
                    ? prev.toolFailed
                    : (cursor.toolFailed ??
                      (cursor.toolCall || cursor.content
                        ? undefined
                        : prev.toolFailed)),
                // Carry the question through display-only cursor refreshes,
                // including from a deduped sibling entry. Fresh activity means
                // the user sent a follow-up or the agent resumed, so the old
                // ask_user card should disappear.
                question: clearsQuestion
                  ? undefined
                  : (prev.question ?? inheritedQuestion),
                pointTarget: isPresentationUpdate
                  ? cursor.pointTarget
                  : (cursor.pointTarget ??
                    (clearsCursorTarget ? undefined : prev.pointTarget)),
                annotationTarget: isPresentationUpdate
                  ? (cursor.annotationTarget ??
                    (preservesPresentationAnnotations
                      ? prev.annotationTarget
                      : undefined))
                  : (cursor.annotationTarget ??
                    (clearsCursorTarget ? undefined : prev.annotationTarget)),
                annotationTargets: isPresentationUpdate
                  ? annotationTargets
                  : (cursor.annotationTargets ??
                    (clearsCursorTarget ? undefined : prev.annotationTargets)),
                presentation: preservesPointPresentation
                  ? prev.presentation
                  : cursor.presentation,
              }
            : {
                ...cursor,
                annotationTargets,
                targetFrameId: resolvedTarget,
                question: clearsQuestion ? undefined : inheritedQuestion,
              };
          merged.presentedToolUseId = presentedToolUseId(prev, cursor);
          retainDesktopInteractionOwnership(merged, cursor, prev);
          finishCursorToolCalls(merged, cursor, prev);
          // The rest point only matters while the run is still going.
          if (merged.frozen || merged.error || merged.question) {
            merged.restPoint = undefined;
          }
          next.set(cursor.agentId, merged);
          return { agentCursors: next };
        }),

      setAgentCursorPoint: (agentId, point) =>
        set((state) => {
          const prev = state.agentCursors.get(agentId);
          if (!prev) return {};
          const next = new Map(state.agentCursors);
          next.set(agentId, {
            ...prev,
            pointTarget: point,
            targetFrameId: null,
            actionSeq: point.seq,
            lastSeen: new Date().toISOString(),
          });
          return { agentCursors: next };
        }),

      acknowledgeAgentCursorResult: (agentId) =>
        set((state) => {
          const cursor = state.agentCursors.get(agentId);
          if (!cursor || cursor.resultAcknowledgedAt === cursor.lastSeen) {
            return state;
          }
          const next = new Map(state.agentCursors);
          next.set(agentId, {
            ...cursor,
            resultAcknowledgedAt: cursor.lastSeen,
          });
          return { agentCursors: next };
        }),

      removeAgentCursor: (agentId, opts) =>
        set((state) => {
          const cursor = state.agentCursors.get(agentId);
          if (!cursor) return state;
          if (
            (cursor.question || isActiveGuideCursor(cursor)) &&
            !opts?.force
          ) {
            return state;
          }
          const next = new Map(state.agentCursors);
          next.delete(agentId);
          return { agentCursors: next };
        }),

      clearAgentCursors: () =>
        set((state) => ({
          agentCursors: new Map(
            [...state.agentCursors].filter(([, cursor]) =>
              isActiveGuideCursor(cursor),
            ),
          ),
        })),

      clearAgentCursorsForSpace: (spaceId) =>
        set((state) => {
          const next = new Map<string, AgentCursor>();
          for (const [id, c] of state.agentCursors) {
            // Question cursors survive space unsubscribe/reconnect cleanup —
            // the pending ask_user card stays until the user answers.
            if (c.spaceId !== spaceId || c.question || isActiveGuideCursor(c)) {
              next.set(id, c);
            }
          }
          return { agentCursors: next };
        }),

      clearPresentedCursors: (
        spaceId,
        presentationDeliveryId,
        executionId,
        completed = false,
      ) =>
        set((state) => {
          let changed = false;
          const next = new Map<string, AgentCursor>();
          for (const [id, cursor] of state.agentCursors) {
            const sameSpace = !spaceId || cursor.spaceId === spaceId;
            const sameDelivery =
              !presentationDeliveryId ||
              cursor.presentationDeliveryId === presentationDeliveryId;
            const sameExecution =
              !executionId || cursor.executionId === executionId;
            if (
              cursor.presentation === "point" &&
              sameSpace &&
              sameDelivery &&
              sameExecution
            ) {
              const previousAnnotations = presentationDeliveryId
                ? agentCursorAnnotationTargets(cursor).filter(
                    (target) =>
                      typeof target.annotationId === "string" &&
                      !target.annotationId.startsWith(
                        `${presentationDeliveryId}:`,
                      ),
                  )
                : [];
              const previousAnnotation =
                previousAnnotations[previousAnnotations.length - 1];
              if (!executionId && previousAnnotation) {
                const anchor = annotationPointerAnchor(previousAnnotation);
                const previousDeliveryId =
                  previousAnnotation.annotationId?.split(":", 1)[0];
                changed = true;
                next.set(id, {
                  ...cursor,
                  ...endPresentation(
                    cursor,
                    completed ? presentationDeliveryId : undefined,
                  ),
                  presentationDeliveryId: previousDeliveryId,
                  presentationNarration: undefined,
                  pointTarget: {
                    displayId: previousAnnotation.displayId,
                    globalX: anchor.x,
                    globalY: anchor.y,
                    seq: previousAnnotation.seq,
                    travelDurationMs: 0,
                  },
                  annotationTarget: previousAnnotation,
                  annotationTargets: previousAnnotations,
                  annotationExclusionTargets: undefined,
                  exclusionFrameIds: undefined,
                  frameExclusionTargets: undefined,
                });
                continue;
              }
              const hasRuntimeState = !!(
                cursor.presentationDeliveryId &&
                (cursor.executionId ||
                  cursor.sessionId ||
                  cursor.conversationFrameId ||
                  cursor.toolCall ||
                  cursor.content ||
                  cursor.contentFull ||
                  cursor.error ||
                  cursor.question ||
                  cursor.frozen)
              );
              if (hasRuntimeState) {
                changed = true;
                next.set(id, {
                  ...cursor,
                  // An execution-wide clear (a clear step, a new run) is not
                  // the end of the presentation's call.
                  ...(presentationDeliveryId
                    ? endPresentation(
                        cursor,
                        completed ? presentationDeliveryId : undefined,
                      )
                    : {}),
                  presentation: undefined,
                  presentationDeliveryId: undefined,
                  presentationNarration: undefined,
                  pointTarget: undefined,
                  restPoint: restPointOf(cursor),
                  annotationTarget: undefined,
                  annotationTargets: undefined,
                  annotationExclusionTargets: undefined,
                  exclusionFrameIds: undefined,
                  frameExclusionTargets: undefined,
                });
              }
              changed = true;
              continue;
            }
            next.set(id, cursor);
          }
          return changed ? { agentCursors: next } : state;
        }),

      finishPresentedCursor: (spaceId, presentationDeliveryId) =>
        set((state) => {
          let changed = false;
          const next = new Map(state.agentCursors);
          for (const [id, cursor] of state.agentCursors) {
            if (
              cursor.presentation !== "point" ||
              (spaceId != null && cursor.spaceId !== spaceId) ||
              cursor.presentationDeliveryId !== presentationDeliveryId
            ) {
              continue;
            }
            changed = true;
            next.set(id, {
              ...cursor,
              ...endPresentation(cursor, presentationDeliveryId),
              pointTarget: undefined,
              restPoint: restPointOf(cursor),
              presentationNarration: undefined,
              annotationExclusionTargets: undefined,
              exclusionFrameIds: undefined,
              frameExclusionTargets: undefined,
            });
          }
          return changed ? { agentCursors: next } : state;
        }),

      clearPresentedAnnotations: (
        spaceId,
        presentationDeliveryId,
        executionId,
      ) =>
        set((state) => {
          let changed = false;
          const next = new Map(state.agentCursors);
          for (const [id, cursor] of state.agentCursors) {
            const sameSpace = !spaceId || cursor.spaceId === spaceId;
            const sameDelivery =
              !presentationDeliveryId ||
              cursor.presentationDeliveryId === presentationDeliveryId;
            const sameExecution =
              !executionId || cursor.executionId === executionId;
            if (
              cursor.presentation !== "point" ||
              !sameSpace ||
              !sameDelivery ||
              !sameExecution ||
              (!cursor.annotationTarget &&
                !cursor.annotationTargets?.length &&
                !cursor.annotationExclusionTargets)
            ) {
              continue;
            }
            changed = true;
            next.set(id, {
              ...cursor,
              annotationTarget: undefined,
              annotationTargets: undefined,
              annotationExclusionTargets: undefined,
            });
          }
          return changed ? { agentCursors: next } : state;
        }),

      clearPresentedFrameExclusions: (spaceId, presentationDeliveryId) =>
        set((state) => {
          let changed = false;
          const next = new Map(state.agentCursors);
          for (const [id, cursor] of state.agentCursors) {
            const sameSpace = !spaceId || cursor.spaceId === spaceId;
            const sameDelivery =
              !presentationDeliveryId ||
              cursor.presentationDeliveryId === presentationDeliveryId;
            if (
              !sameSpace ||
              !sameDelivery ||
              (!cursor.exclusionFrameIds && !cursor.frameExclusionTargets)
            ) {
              continue;
            }
            changed = true;
            next.set(id, {
              ...cursor,
              exclusionFrameIds: undefined,
              frameExclusionTargets: undefined,
            });
          }
          return changed ? { agentCursors: next } : state;
        }),

      setAgentCursorQuestion: (agentId, question, fallback) =>
        set((state) => {
          const prev = state.agentCursors.get(agentId);
          if (!prev) {
            if (!fallback) {
              // Live `cursor.question` path: cursor must already exist
              // (created via cursor.move). Without it we don't have
              // coordinates to anchor against.
              console.warn(
                `[setAgentCursorQuestion] no cursor for agent ${agentId}; skipping`,
              );
              return state;
            }
            // Rehydration path: build a minimal idle cursor entry that
            // holds the question. The cursor renders as idle (no
            // targetFrameId). When a real cursor.move arrives, setAgentCursor
            // merges and preserves this question.
            const next = new Map(state.agentCursors);
            next.set(agentId, {
              agentId,
              spaceId: fallback.spaceId,
              name: fallback.name,
              color: fallback.color,
              targetFrameId: null,
              lastSeen: new Date().toISOString(),
              question: question ?? undefined,
            });
            return { agentCursors: next };
          }
          const next = new Map(state.agentCursors);
          next.set(agentId, {
            ...prev,
            question: question
              ? keepAskedPoint(question, prev.question)
              : undefined,
            // A question pauses the run: the cursor lands beside the user.
            restPoint: question ? undefined : prev.restPoint,
          });
          return { agentCursors: next };
        }),

      clearAgentCursorQuestion: (agentId) =>
        set((state) => {
          const prev = state.agentCursors.get(agentId);
          if (!prev || prev.question === undefined) return state;
          const next = new Map(state.agentCursors);
          const questionTool = isCursorQuestionTool(prev);
          next.set(agentId, {
            ...prev,
            question: undefined,
            ...(questionTool
              ? {
                  toolCall: undefined,
                  toolCallLabel: undefined,
                  toolName: undefined,
                  toolFailed: undefined,
                  endedToolUseId: prev.toolUseId,
                }
              : {}),
          });
          return { agentCursors: next };
        }),

      addPendingPermission: (permission) =>
        set((state) => {
          const next = new Map(state.pendingPermissions);
          next.set(permission.executionId, permission);
          return { pendingPermissions: next };
        }),

      removePendingPermission: (executionId) =>
        set((state) => {
          const next = new Map(state.pendingPermissions);
          next.delete(executionId);
          return { pendingPermissions: next };
        }),

      addAwaitingInput: (awaiting) =>
        set((state) => {
          const next = new Map(state.awaitingInputs);
          const existing = next.get(awaiting.frameId) ?? [];
          // Dedup by id — re-broadcasts / reconnect replays shouldn't stack.
          const withoutDup = existing.filter((a) => a.id !== awaiting.id);
          next.set(awaiting.frameId, [...withoutDup, awaiting]);
          return { awaitingInputs: next };
        }),

      removeAwaitingInput: (awaitingInputId) => {
        set((state) => {
          const next = new Map(state.awaitingInputs);
          for (const [frameId, list] of next.entries()) {
            const filtered = list.filter((a) => a.id !== awaitingInputId);
            if (filtered.length !== list.length) {
              if (filtered.length === 0) next.delete(frameId);
              else next.set(frameId, filtered);
            }
          }
          return { awaitingInputs: next };
        });
        useInputBarQuestionStore.getState().resolve(awaitingInputId);
      },

      markAgentDeferred: (executionId, agentTemplateId) =>
        set((state) => {
          const nextExec = new Map(state.deferredExecutionAgents);
          nextExec.set(executionId, agentTemplateId);
          const nextIds = new Set(state.deferredAgentTemplateIds);
          nextIds.add(agentTemplateId);
          return {
            deferredAgentTemplateIds: nextIds,
            deferredExecutionAgents: nextExec,
          };
        }),

      clearAgentDeferredByExecution: (executionId) =>
        set((state) => {
          const agentTemplateId =
            state.deferredExecutionAgents.get(executionId);
          if (!agentTemplateId) return {};
          const nextExec = new Map(state.deferredExecutionAgents);
          nextExec.delete(executionId);
          // Only clear the agent badge if no OTHER deferred execution still
          // references the same agent_template_id. Multiple deferred steps for
          // the same agent should only clear the dot when all of them resume.
          const stillDeferred = Array.from(nextExec.values()).includes(
            agentTemplateId,
          );
          if (stillDeferred) {
            return { deferredExecutionAgents: nextExec };
          }
          const nextIds = new Set(state.deferredAgentTemplateIds);
          nextIds.delete(agentTemplateId);
          return {
            deferredAgentTemplateIds: nextIds,
            deferredExecutionAgents: nextExec,
          };
        }),

      loadConnections: (connections) =>
        set({ edges: connections.map(connectionToEdge) }),

      setFocusHiddenFrames: (frameIds) =>
        set((state) => {
          if (import.meta.env.DEV) {
            console.info(
              `[frame-visibility] setFocusHiddenFrames → ${frameIds.length} frame(s) ephemerally hidden`,
            );
          }
          const focusHiddenFrameIds = new Set(frameIds);
          return {
            focusHiddenFrameIds,
            nodes: state.nodes.map((n) => {
              const hidden =
                !!n.data.metadata?.hidden || focusHiddenFrameIds.has(n.id);
              return hidden === n.hidden ? n : { ...n, hidden };
            }),
          };
        }),

      addConnection: (conn) =>
        set((state) => {
          if (state.edges.some((e) => e.id === conn.id)) return state;
          return { edges: [...state.edges, connectionToEdge(conn)] };
        }),

      removeConnection: (connId) =>
        set((state) => ({
          edges: state.edges.filter((e) => e.id !== connId),
        })),

      setNodes: (nodes) => set({ nodes }),
      setEdges: (edges) => set({ edges }),
      setViewport: (viewport) => set({ viewport }),

      // The presentation and selection the canvas showed end with it, after
      // the canvas.
      clear: (kept) => {
        set({
          nodes: [],
          edges: [],
          viewport: DEFAULT_VIEWPORT,
          multiSelectContextMenu: null,
          presentationDimsSuppressedFrameIds: new Set<string>(),
          presentationRestore: null,
          isPanMode: false,
          commentModeActive: false,
          notePlacementActive: false,
          commentPopover: null,
          agentCursors: kept?.agentCursors ?? new Map(),
          pendingPermissions: kept?.pendingPermissions ?? new Map(),
          awaitingInputs: kept?.awaitingInputs ?? new Map(),
          deferredAgentTemplateIds: new Set<string>(),
          deferredExecutionAgents: new Map<string, string>(),
          cursorInput: null,
          pendingCursorSummon: null,
          focusHiddenFrameIds: new Set<string>(),
        });
        usePresentationStore.getState().setPresentation(null);
        useSelectionStore.getState().select(null);
      },
    })),
  ),
);

followFramesStore(useCanvasStore);
